import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ApiErrorState } from '../components/ApiErrorState.tsx';
import { Cargando } from '../components/FullScreenMessage.tsx';
import { NotFoundPage } from './NotFoundPage.tsx';
import { MatchCard } from '../features/predictions/MatchCard.tsx';
import { esPorJugar } from '../features/predictions/estado.ts';
import { RachaBadge } from '../features/standings/RachaBadge.tsx';
import { useLeagueMatches, useRefrescoAlKickoff } from '../features/predictions/hooks.ts';
import { useLeagueDetail } from '../features/leagues/hooks.ts';

type Filtro = 'all' | 'upcoming' | 'played';

export function PredictionsPage() {
  const { t } = useTranslation();
  const { leagueId } = useParams<{ leagueId: string }>();
  const [filtro, setFiltro] = useState<Filtro>('all');

  const liga = useLeagueDetail(leagueId ?? '');
  const { data, isPending, error, refetch } = useLeagueMatches(leagueId ?? '');
  const matches = data?.matches;

  const refrescar = useCallback(() => {
    void refetch();
  }, [refetch]);
  useRefrescoAlKickoff(matches, refrescar);

  if (!leagueId) return <NotFoundPage />;
  // El 404 de privacidad de la liga llega por cualquiera de las dos consultas.
  if (error) return <ApiErrorState error={error} />;
  if (liga.error) return <ApiErrorState error={liga.error} />;
  if (isPending) return <Cargando text={t('common.loading')} />;

  const visibles = (matches ?? []).filter((m) => {
    if (filtro === 'upcoming') return esPorJugar(m);
    if (filtro === 'played') return !esPorJugar(m);
    return true;
  });

  return (
    <>
      <header className="mb-4">
        <Link to={`/leagues/${leagueId}`} className="text-sm text-tenue">
          ← {liga.data?.league.name ?? t('common.back')}
        </Link>
        <h1 className="mt-1 text-xl font-bold">{t('predictions.title')}</h1>

        {/*
          La racha viva del usuario. Es el mismo número que produjo sus multiplicadores:
          lo calcula el motor y aquí solo se pinta. Se enseña SIEMPRE, también cuando vale
          0 — saber que la has perdido es tan informativo como saber que la tienes.
        */}
        <p className="mt-2 flex items-center gap-2 text-sm text-tenue" data-test="mi-racha">
          <span>
            {t('streak.label')}:{' '}
            <span
              className="font-bold text-texto"
              data-test="racha-valor"
              data-racha={data?.current_streak ?? 0}
            >
              {data?.current_streak ?? 0}
            </span>
          </span>
          <RachaBadge
            racha={data?.current_streak ?? 0}
            umbrales={data?.streak_thresholds ?? []}
            variante="completo"
          />
        </p>
      </header>

      <div className="mb-4 flex gap-1" role="tablist" aria-label={t('predictions.title')}>
        {(['all', 'upcoming', 'played'] as const).map((clave) => (
          <button
            key={clave}
            type="button"
            role="tab"
            aria-selected={filtro === clave}
            onClick={() => setFiltro(clave)}
            className={[
              'min-h-toque flex-1 rounded-xl border px-3 py-2 text-sm',
              filtro === clave
                ? 'border-acento bg-superficie-alta font-semibold text-acento'
                : 'border-borde text-tenue',
            ].join(' ')}
            data-test={`filtro-${clave}`}
          >
            {t(`predictions.filter.${clave}`)}
          </button>
        ))}
      </div>

      {visibles.length === 0 ? (
        <p className="tarjeta text-center text-tenue" data-test="sin-partidos">
          {t('predictions.empty')}
        </p>
      ) : null}

      <div className="space-y-3" data-test="lista-partidos">
        {visibles.map((match) => (
          <MatchCard key={match.match_id} leagueId={leagueId} match={match} />
        ))}
      </div>
    </>
  );
}
